<template>
  <!-- 下拉菜单模式 -->
  <div 
    v-if="mode === 'dropdown'" 
    ref="dropdownRef" 
    class="filter-dropdown"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="filter-trigger" @click="toggleDropdown">
      <span>{{ modelValue }}</span>
      <!-- 向下/向上箭头 SVG -->
      <svg 
        class="arrow-icon" 
        :class="{ 'arrow-up': showDropdown }"
        viewBox="0 0 12 12" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <transition name="dropdown-fade">
      <div v-if="showDropdown" class="dropdown-menu">
        <div
          v-for="option in options"
          :key="option"
          class="dropdown-option"
          :class="{ active: modelValue === option }"
          @click="selectOption(option)"
        >
          {{ option }}
        </div>
      </div>
    </transition>
  </div>

  <!-- 按钮组模式 -->
  <div v-else-if="mode === 'buttons'" class="filter-buttons">
    <button 
      v-for="option in options"
      :key="option"
      class="filter-btn"
      :class="{ active: modelValue === option }"
      @click="selectOption(option)"
    >
      {{ option }}
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '完整'
  },
  mode: {
    type: String,
    default: 'dropdown',
    validator: (value) => ['dropdown', 'buttons'].includes(value)
  },
  options: {
    type: Array,
    default: () => ['完整', '热门', '竞彩', '北单']
  }
})

const emit = defineEmits(['update:modelValue'])

// 组件根元素引用
const dropdownRef = ref(null)

// 下拉菜单显示状态（仅下拉模式使用）
const showDropdown = ref(false)

/**
 * 切换下拉菜单显示状态
 */
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
}

/**
 * 选择选项
 */
const selectOption = (option) => {
  emit('update:modelValue', option)
  // 下拉模式下选择后关闭菜单
  if (props.mode === 'dropdown') {
    showDropdown.value = false
  }
}

/**
 * 鼠标进入时显示下拉菜单
 */
const handleMouseEnter = () => {
  showDropdown.value = true
}

/**
 * 鼠标离开时隐藏下拉菜单
 */
const handleMouseLeave = () => {
  showDropdown.value = false
}

/**
 * 点击外部区域关闭下拉菜单
 */
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false
  }
}

// 生命周期钩子
onMounted(() => {
  // 只在下拉模式下添加全局点击监听
  if (props.mode === 'dropdown') {
    document.addEventListener('click', handleClickOutside)
  }
})

onUnmounted(() => {
  // 清理事件监听
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">
// 下拉菜单模式样式
.filter-dropdown {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;

  .filter-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    cursor: pointer;

    span {
      margin-right: 4px;
      transition: color 0.2s ease;
    }

    .arrow-icon {
      width: 12px;
      height: 12px;
      color: #afb8cb;
      transition: all 0.3s ease;
      margin-left: 4px;
      
      // 向上箭头（旋转180度）
      &.arrow-up {
        transform: rotate(180deg);
      }
    }

    &:hover {
      span {
        color: $color-primary;
      }
      
      .arrow-icon {
        color: $color-primary;
      }
    }
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: $scoreFootball-white;
    border-radius: 8px;
    border: 1px solid $scoreFootball-border-light;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
    overflow: hidden;

    .dropdown-option {
      padding: 8px 12px;
      font-size: 12px;
      color: #afb8cb;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;

      &:hover {
        background: rgba($color-primary, 0.08);
        color: $color-primary;
        font-weight: normal;
      }

      &.active {
        background: $scoreFootball-white;
        color: #afb8cb;
        font-weight: bold;
        
        &:hover {
          background: rgba($color-primary, 0.08);
          color: $color-primary;
          font-weight: normal;
        }
      }
    }
  }
}

// 下拉菜单过渡动画
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

// 按钮组模式样式
.filter-buttons {
  display: flex;
  gap: 8px;
  width: 100%;

  .filter-btn {
    flex: 1;
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 500;
    color: $scoreFootball-text-secondary;
    background: $scoreFootball-white;
    border: 1px solid $scoreFootball-border-light;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;

    &.active {
      background: $color-primary;
      color: $text-white;
      border-color: $color-primary;
    }

    &:hover {
      border-color: $color-primary;
      color: $color-primary;
      
      &.active {
        background: $color-primary-dark;
        color: $text-white;
      }
    }
  }
}
</style>
